import { format, parseISO } from 'date-fns';
import { groupBy, max, sortBy } from 'rambda';
import { useMemo } from 'react';
import { Events, EventTimeslots, SubEvents } from 'src/graphql/schema.graphql';
import {
  TranslatedItem,
  useTranslateItem,
} from 'src/hooks/directus/useTranslateItem';
import { EventTicket } from 'src/models/directus';
import { useEventTicketQuery } from 'src/modules/events/hooks/useEventTicketQuery';
import {
  EventAgendaTimeslot,
  EventAgendaSchedule,
} from 'src/modules/events/models';

export const mapSubEventTimeslotToAgendaTimeslot = (
  timeslot: EventTimeslots,
  subEvent: TranslatedItem<SubEvents>,
  ticket: EventTicket | undefined,
): EventAgendaTimeslot | null => {
  if (!timeslot || !timeslot.startDate || !timeslot.endDate) {
    return null;
  }

  const participantsLimit = subEvent.guestLimit ?? 0;
  const participants = timeslot.eventParticipants_func?.count ?? 0;

  return {
    id: timeslot.id,
    availableSeats: max(0, participantsLimit - participants),
    participants,
    participantsLimit,
    requireRegistration: !!subEvent.requireRegistration,
    speakers: subEvent.speakers,
    startDate: timeslot.startDate,
    endDate: timeslot.endDate,
    logo: subEvent.event?.image?.id,
    title: subEvent.title,
    description: subEvent.description,
    location: subEvent.location,
    ticket: (ticket?.timeslots ?? []).includes(timeslot.id)
      ? ticket?.ticket
      : undefined,
  };
};

export const useEventAgendaSchedule = (
  agenda: Pick<Events, 'translations' | 'subEvents'>,
  eventId: string,
): EventAgendaSchedule => {
  const translateFn = useTranslateItem();

  const { data: ticket } = useEventTicketQuery(eventId);

  return useMemo(() => {
    const timeslots: EventAgendaTimeslot[] =
      agenda?.subEvents
        ?.flatMap((subEvent) =>
          subEvent?.eventTimeslots?.map((timeslot) =>
            mapSubEventTimeslotToAgendaTimeslot(
              timeslot,
              translateFn(subEvent),
              ticket,
            ),
          ),
        )
        .filter((m): m is EventAgendaTimeslot => !!m) ?? [];

    const groupedTimeslotsByDay = groupBy(
      (m) => format(parseISO(m.startDate), 'MMMM do'),
      timeslots,
    );

    return sortBy(
      (d) => parseISO(d.timeslots[0].startDate),
      Object.keys(groupedTimeslotsByDay).map((date) => ({
        id: `day-${format(
          parseISO(groupedTimeslotsByDay[date][0].startDate),
          'dd-MM-yyyy',
        )}`,
        label: date,
        longLabel: format(
          parseISO(groupedTimeslotsByDay[date][0].startDate),
          'EEEE, d.LL.yyyy',
        ),
        timeslots: sortBy(
          (m) => parseISO(m.startDate),
          groupedTimeslotsByDay[date],
        ),
      })),
    );
  }, [agenda, ticket, translateFn]);
};
